import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, HostListener, input, output, signal } from '@angular/core';

/** Company logo variant options */
export type CompanyLogoVariant = 'full' | 'horizontal' | 'icon';

/** Product identifier options */
export type ProductType = 'symphony' | 'codis' | 'phoenix' | 'apollo' | 'scheduling' | 'engage';

/** Predefined size options for logo display */
export type LogoSize = 'xsmall' | 'small' | 'medium' | 'large' | 'x-large' | '2x-large' | '3x-large' | '4x-large';

/** Logo type discriminator */
export type LogoType = 'company' | 'product';

/** Configuration for company logo display */
export interface CompanyLogoConfig {
  type: 'company';
  variant: CompanyLogoVariant;
}

/** Configuration for product logo display */
export interface ProductLogoConfig {
  type: 'product';
  product: ProductType;
}

/** Discriminated union of all possible logo configurations */
export type LogoConfig = CompanyLogoConfig | ProductLogoConfig;

/** Payload for logo click events */
export interface LogoClickEvent {
  type: LogoType;
  variant?: CompanyLogoVariant;
  product?: ProductType;
  timestamp: Date;
  nativeEvent: MouseEvent | KeyboardEvent;
}

/** Payload for logo error events */
export interface LogoErrorEvent {
  type: LogoType;
  variant?: CompanyLogoVariant;
  product?: ProductType;
  imagePath: string;
  error: Error;
  timestamp: Date;
}

/** Base path for branding assets */
const BRANDING_ASSET_PATH = '/branding';

/** Mapping of company logo variants to SVG file paths */
const COMPANY_LOGO_PATHS: Record<CompanyLogoVariant, string> = {
  full: `${BRANDING_ASSET_PATH}/company/complexcare-full.svg`,
  horizontal: `${BRANDING_ASSET_PATH}/company/complexcare-horizontal.svg`,
  icon: `/assets/images/ccs-logo.svg`,
};

/** Mapping of product types to SVG file paths */
const PRODUCT_LOGO_PATHS: Record<ProductType, string> = {
  symphony: `${BRANDING_ASSET_PATH}/products/symphony.svg`,
  codis: `${BRANDING_ASSET_PATH}/products/codis.svg`,
  phoenix: `${BRANDING_ASSET_PATH}/products/phoenix.svg`,
  apollo: `${BRANDING_ASSET_PATH}/products/apollo.svg`,
  scheduling: `${BRANDING_ASSET_PATH}/company/complexcare-full.svg`,
  engage: `${BRANDING_ASSET_PATH}/products/engage.svg`,
};

/**
 * HxLogo - Parent Branding Logo Component
 *
 * Generic logo component that displays Complex Care Solutions company logos and product logos with support for multiple
 * variants, sizes, and accessibility features.
 *
 * **Note**: For simpler usage, consider using the specialized child components:
 *
 * - `<hx-complexcare-logo>` for company logos
 * - `<hx-symphony-logo>` for Symphony product logo
 * - `<hx-codis-logo>` for CODIS product logo
 * - `<hx-phoenix-logo>` for Phoenix product logo
 *
 * @example
 *   ```html
 *   <!-- Company logo -->
 *   <hx-logo type="company" variant="full" size="medium" />
 *
 *   <!-- Product logo -->
 *   <hx-logo type="product" product="symphony" size="large" />
 *   ```;
 */
@Component({
  selector: 'hx-logo',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './logo.html',
  styleUrls: ['./logo.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxLogo {
  /**
   * Logo type: company or product
   *
   * @default 'company'
   */
  readonly type = input<LogoType>('company');

  /**
   * Company logo variant (only valid when type='company')
   *
   * @default 'full'
   */
  readonly variant = input<CompanyLogoVariant>('full');

  /** Product identifier (only valid when type='product') */
  readonly product = input<ProductType | undefined>(undefined);

  /**
   * Predefined size option
   *
   * @default 'medium'
   */
  readonly size = input<LogoSize>('medium');

  /** Custom width (CSS length value) Overrides size prop when provided */
  readonly width = input<string | undefined>(undefined);

  /** Custom height (CSS length value) Overrides size prop when provided */
  readonly height = input<string | undefined>(undefined);

  /** Custom alt text for accessibility If not provided, generates descriptive alt text automatically */
  readonly alt = input<string | undefined>(undefined);

  /** Additional CSS classes to apply to logo */
  readonly cssClass = input<string>('');

  /**
   * Whether the logo should be clickable/interactive
   *
   * @default false
   */
  readonly clickable = input<boolean>(false);

  /** ARIA label for additional context */
  readonly ariaLabel = input<string | undefined>(undefined);

  /**
   * Tab index for keyboard navigation
   *
   * @default 0
   */
  readonly tabIndex = input<number>(0);

  /**
   * Enable subtle hover animation (scale effect)
   *
   * @default false
   */
  readonly hoverEffect = input<boolean>(false);

  /** Emitted when user clicks on a clickable logo */
  readonly logoClick = output<LogoClickEvent>();

  /** Emitted when SVG image fails to load */
  readonly logoError = output<LogoErrorEvent>();

  /** Internal state: whether to show fallback display */
  protected readonly showFallback = signal(false);

  /** Computed: Resolved file path to SVG asset */
  protected readonly logoPath = computed(() => {
    if (this.type() === 'company') {
      return COMPANY_LOGO_PATHS[this.variant()];
    } else {
      const prod = this.product();
      if (prod) {
        return PRODUCT_LOGO_PATHS[prod];
      }
      console.warn('HxLogo: product type requires product prop');
      return COMPANY_LOGO_PATHS.full; // fallback
    }
  });

  /** Computed: Generated or custom alt text */
  protected readonly logoAlt = computed(() => {
    if (this.alt()) {
      return this.alt();
    }

    if (this.type() === 'company') {
      const variantText = this.variant() === 'full' ? 'full' : this.variant() === 'horizontal' ? 'horizontal' : 'icon';
      return `Complex Care Solutions ${variantText} logo`;
    } else {
      const prod = this.product();
      if (prod) {
        return `${prod.charAt(0).toUpperCase() + prod.slice(1)} product logo`;
      }
      return 'Company logo';
    }
  });

  /** Computed: CSS class string */
  protected readonly logoClasses = computed(() => {
    const classes = ['hx-logo'];

    // Type-specific class
    classes.push(`hx-logo--${this.type()}`);

    // Size class (if no custom dimensions)
    if (!this.width() && !this.height()) {
      classes.push(`hx-logo--${this.size()}`);
    }

    // Clickable class
    if (this.clickable()) {
      classes.push('hx-logo--clickable');
    }

    // Hover effect class
    if (this.hoverEffect()) {
      classes.push('hx-logo--hover-effect');
    }

    // Fallback class
    if (this.showFallback()) {
      classes.push('hx-logo--fallback');
    }

    // Custom classes
    if (this.cssClass()) {
      classes.push(this.cssClass());
    }

    return classes.join(' ');
  });

  /** Computed: Width style */
  protected readonly logoWidth = computed(() => {
    return this.width() || undefined;
  });

  /** Computed: Height style */
  protected readonly logoHeight = computed(() => {
    return this.height() || undefined;
  });

  /** Handle image load errors */
  @HostListener('error', ['$event'])
  protected handleImageError(event: Event): void {
    const error = new Error(`Failed to load logo: ${this.logoPath()}`);
    console.error(error.message);

    this.showFallback.set(true);

    this.logoError.emit({
      type: this.type(),
      variant: this.type() === 'company' ? this.variant() : undefined,
      product: this.type() === 'product' ? this.product() : undefined,
      imagePath: this.logoPath(),
      error,
      timestamp: new Date(),
    });
  }

  /** Handle click events on clickable logos */
  @HostListener('click', ['$event'])
  protected handleClick(event: MouseEvent): void {
    if (this.clickable()) {
      this.emitClickEvent(event);
    }
  }

  /** Handle keyboard events on clickable logos */
  @HostListener('keydown', ['$event'])
  protected handleKeydown(event: KeyboardEvent): void {
    if (this.clickable() && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.emitClickEvent(event);
    }
  }

  /** Emit logo click event */
  private emitClickEvent(nativeEvent: MouseEvent | KeyboardEvent): void {
    this.logoClick.emit({
      type: this.type(),
      variant: this.type() === 'company' ? this.variant() : undefined,
      product: this.type() === 'product' ? this.product() : undefined,
      timestamp: new Date(),
      nativeEvent,
    });
  }

  /** Validate configuration and log warnings */
  constructor() {
    if (typeof window !== 'undefined') {
      // Client-side validation
      if (this.type() === 'company' && !this.variant()) {
        console.warn('HxLogo: type="company" should specify variant prop');
      }
      if (this.type() === 'product' && !this.product()) {
        console.warn('HxLogo: type="product" requires product prop');
      }
    }
  }
}
